import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Info,
  Target,
  X,
} from 'lucide-react';
import type {
  DiagnosisCategory,
  DiagnosisFinding,
  DiagnosisSeverity,
  DiagnosisType,
} from '@/domain/diagnosis/contracts';
import { confidenceBand } from '@/domain/diagnosis/coverage';
import { buildActionPlan } from '@/domain/diagnosis/action-plan';
import { useEditorStore } from '@/features/funnel-editor/state/editor-store';
import { useDiagnosis } from '@/features/funnel-editor/diagnosis/use-diagnosis';
import { useSimulation } from '@/features/funnel-editor/simulation/use-simulation';
import { localizeSystemValue } from '@/app/localization';

const severityStyles: Record<DiagnosisSeverity, string> = {
  critical: 'border-[rgba(226,85,85,0.45)] bg-[rgba(226,85,85,0.08)]',
  attention: 'border-[rgba(224,184,102,0.45)] bg-[rgba(224,184,102,0.06)]',
  positive: 'border-[rgba(75,181,125,0.4)] bg-[rgba(75,181,125,0.06)]',
  info: 'border-border-subtle bg-surface-2',
};

function FindingIcon({ severity }: { severity: DiagnosisSeverity }) {
  if (severity === 'critical' || severity === 'attention')
    return <AlertTriangle aria-hidden size={16} />;
  if (severity === 'positive') return <CheckCircle2 aria-hidden size={16} />;
  return <Info aria-hidden size={16} />;
}

export function FindingCard({
  finding,
  onExplain,
}: {
  finding: DiagnosisFinding;
  onExplain?: (findingId: string) => void;
}) {
  const setSelection = useEditorStore((state) => state.setSelection);
  const setEdgeSelection = useEditorStore((state) => state.setEdgeSelection);
  return (
    <article
      className={`rounded border p-3 ${severityStyles[finding.severity]}`}
      aria-label={`${localizeSystemValue(finding.severity)}: ${finding.title}`}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5">
          <FindingIcon severity={finding.severity} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-text-primary">{finding.title}</h3>
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              {localizeSystemValue(finding.severity)} · confiança {localizeSystemValue(confidenceBand(finding.confidence))}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-secondary">{finding.summary}</p>
          {finding.recommendation ? (
            <p className="mt-2 text-xs text-text-primary">
              <ChevronRight aria-hidden className="mr-1 inline" size={12} />
              {finding.recommendation}
            </p>
          ) : null}
          <details className="mt-2 text-xs text-text-secondary">
            <summary className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
              Por que este achado existe
            </summary>
            <ul className="mt-2 space-y-1 pl-4">
              {finding.evidence.map((item, index) => (
                <li key={`${item.metric}-${index}`}>
                  {item.explanation}{' '}
                  <span className="text-text-muted">
                    ({item.metric}: {item.value === null ? 'desconhecido' : String(item.value)})
                  </span>
                </li>
              ))}
            </ul>
            {finding.caveats.map((caveat) => (
              <p key={caveat} className="mt-1 text-text-muted">
                Ressalva: {caveat}
              </p>
            ))}
            <p className="mt-1 text-text-muted">Regra: {finding.ruleId}</p>
            <p className="mt-1 text-text-muted">
              Base da confiança: completude{' '}
              {Math.round(finding.confidenceComponents.completeness * 100)}%, qualidade das evidências{' '}
              {Math.round(finding.confidenceComponents.evidenceQuality * 100)}%, qualidade da origem{' '}
              {Math.round(finding.confidenceComponents.originQuality * 100)}%, clareza da topologia{' '}
              {Math.round(finding.confidenceComponents.topologyClarity * 100)}%.
            </p>
          </details>
          {finding.affectedNodeIds.length || finding.affectedEdgeIds.length ? (
            <button
              type="button"
              className="mt-2 rounded border border-border-subtle px-2 py-1 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              onClick={() => {
                if (finding.affectedNodeIds.length) setSelection(finding.affectedNodeIds);
                else setEdgeSelection(finding.affectedEdgeIds);
              }}
            >
              Focar elementos afetados
            </button>
          ) : null}
          {onExplain ? (
            <button
              type="button"
              className="ml-2 mt-2 rounded border border-border-brand px-2 py-1 text-xs text-text-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              onClick={() => onExplain(finding.id)}
            >
              Explicar com IA
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

const FILTERS: Array<{ value: 'all' | DiagnosisType; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'bottleneck', label: 'Gargalos' },
  { value: 'risk', label: 'Riscos' },
  { value: 'opportunity', label: 'Oportunidades' },
  { value: 'data_gap', label: 'Lacunas de dados' },
  { value: 'goal_gap', label: 'Metas' },
];

export function DiagnosisPanel({
  onClose,
  onExplainFinding,
}: {
  onClose: () => void;
  onExplainFinding?: (findingId: string) => void;
}) {
  const diagnosis = useDiagnosis();
  const simulation = useSimulation();
  const setActiveScenario = useEditorStore((state) => state.setActiveScenario);
  const ensureSimulationDefaults = useEditorStore((state) => state.ensureSimulationDefaults);
  const [filter, setFilter] = useState<'all' | DiagnosisType>('all');
  const [severity, setSeverity] = useState<'all' | DiagnosisSeverity>('all');
  const [category, setCategory] = useState<'all' | DiagnosisCategory>('all');
  const findings = useMemo(
    () =>
      diagnosis?.result.items.filter(
        (item) =>
          (filter === 'all' || item.type === filter) &&
          (severity === 'all' || item.severity === severity) &&
          (category === 'all' || item.category === category),
      ) ?? [],
    [category, diagnosis, filter, severity],
  );
  useEffect(() => {
    if (simulation && simulation.extension.scenarios.length === 0) ensureSimulationDefaults();
  }, [ensureSimulationDefaults, simulation]);
  if (!diagnosis)
    return (
      <aside aria-label="Diagnóstico" className="w-[440px] border-l border-border-subtle p-4">
        <p role="status">Preparando diagnóstico…</p>
      </aside>
    );
  const { result } = diagnosis;
  const actions = buildActionPlan(result.items, result.coverage.overall);
  return (
    <aside
      aria-label="Diagnóstico"
      className="w-[440px] shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold">
          <BarChart3 size={17} /> Diagnóstico
        </h2>
        <button type="button" aria-label="Fechar diagnóstico" onClick={onClose}>
          <X size={17} />
        </button>
      </div>
      <p className="mt-1 text-xs text-text-muted">
        {result.context.scenario.name} · recalculado a partir do grafo atual
      </p>
      {simulation?.extension.scenarios.length ? (
        <label className="mt-3 block text-xs text-text-secondary">
          Cenário do diagnóstico
          <select
            aria-label="Cenário do diagnóstico"
            className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5 text-text-primary"
            value={simulation.extension.activeScenarioId ?? ''}
            onChange={(event) => setActiveScenario(event.target.value || null)}
          >
            <option value="">Base</option>
            {simulation.extension.scenarios.map((scenario) => (
              <option key={scenario.id} value={scenario.id}>
                {scenario.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <section aria-label="Resumo do diagnóstico" className="mt-4 grid grid-cols-4 gap-2">
        {[
          ['Críticos', result.summary.critical],
          ['Atenção', result.summary.attention],
          ['Oportunidades', result.summary.opportunities],
          ['Lacunas de dados', result.summary.dataGaps],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded border border-border-subtle p-2 text-center">
            <strong className="block text-base">{value}</strong>
            <span className="text-[10px] text-text-muted">{label}</span>
          </div>
        ))}
      </section>
      <section
        aria-label="Pontuações do diagnóstico"
        className="mt-3 rounded border border-border-subtle p-3"
      >
        <div className="flex justify-between text-xs">
          <span>Cobertura</span>
          <strong>{Math.round(result.coverage.overall * 100)}%</strong>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded bg-surface-2">
          <div className="h-full bg-brand" style={{ width: `${result.coverage.overall * 100}%` }} />
        </div>
        <p className="mt-2 text-xs text-text-muted">
          Pontuação geral:{' '}
          {result.scores.overall === undefined
            ? 'Desconhecida — aguardando pesos aprovados ou cobertura suficiente'
            : `${result.scores.overall.toFixed(1)} / 100`}
        </p>
        <ul className="mt-2 grid grid-cols-2 gap-2 text-xs">
          {result.scores.breakdown.map((dimension) => (
            <li key={dimension.dimension} className="rounded bg-surface-2 p-2">
              <span className="block capitalize text-text-secondary">{localizeSystemValue(dimension.dimension)}</span>
              <strong>
                {dimension.score === undefined ? 'Desconhecida' : dimension.score.toFixed(1)}
              </strong>
              <span className="ml-1 text-[10px] text-text-muted">
                {Math.round(dimension.coverage * 100)}% de cobertura ·{' '}
                confiança {localizeSystemValue(confidenceBand(dimension.confidence))}
              </span>
            </li>
          ))}
        </ul>
      </section>
      {result.warnings.map((warning) => (
        <p role="note" key={warning} className="mt-2 text-xs text-[#e0b866]">
          {warning}
        </p>
      ))}
      <div className="mt-4 flex flex-wrap gap-1" role="group" aria-label="Filtros de achados">
        {FILTERS.map((item) => (
          <button
            type="button"
            key={item.value}
            aria-pressed={filter === item.value}
            onClick={() => setFilter(item.value)}
            className={`rounded border px-2 py-1 text-xs ${filter === item.value ? 'border-border-brand text-text-brand' : 'border-border-subtle text-text-secondary'}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="text-[10px] uppercase tracking-wide text-text-muted">
          Severidade
          <select
            aria-label="Filtrar por severidade"
            className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1 text-xs text-text-primary"
            value={severity}
            onChange={(event) => setSeverity(event.target.value as typeof severity)}
          >
            <option value="all">Todas as severidades</option>
            <option value="critical">Crítica</option>
            <option value="attention">Atenção</option>
            <option value="positive">Positiva</option>
            <option value="info">Info</option>
          </select>
        </label>
        <label className="text-[10px] uppercase tracking-wide text-text-muted">
          Categoria
          <select
            aria-label="Filtrar por categoria"
            className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1 text-xs text-text-primary"
            value={category}
            onChange={(event) => setCategory(event.target.value as typeof category)}
          >
            <option value="all">Todas as categorias</option>
            {[
              'acquisition',
              'conversion',
              'commercial',
              'retention',
              'automation',
              'financial',
              'data_quality',
              'structure',
            ].map((value) => (
              <option key={value} value={value}>
                {localizeSystemValue(value)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-3 space-y-2">
        {findings.length ? (
          findings.map((finding) => (
            <FindingCard key={finding.id} finding={finding} onExplain={onExplainFinding} />
          ))
        ) : (
          <p className="rounded border border-border-subtle p-4 text-center text-xs text-text-muted">
            {result.coverage.overall < 0.4
              ? 'Não há dados suficientes para um diagnóstico amplo. Adicione as entradas ausentes; valores desconhecidos não foram tratados como zero.'
              : 'Nenhum problema relevante foi encontrado neste filtro com as evidências atuais. Isso não é uma garantia da saúde do funil.'}
          </p>
        )}
      </div>
      <section aria-label="Plano de ação" className="mt-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Target size={15} /> Plano de ação
        </h3>
        <ol className="mt-2 space-y-2">
          {actions.items.slice(0, 5).map((action) => (
            <li key={action.id} className="rounded border border-border-subtle p-3 text-xs">
              <div className="flex justify-between gap-2">
                <strong>{action.title}</strong>
                <span className="text-text-muted">
                  {action.priority ? `P${action.priority}` : 'Sem classificação'}
                </span>
              </div>
              <p className="mt-1 text-text-secondary">{action.reason}</p>
              <p className="mt-1 text-text-muted">
                Impacto {localizeSystemValue(action.impact)} · esforço {localizeSystemValue(action.effort)} · urgência desconhecida
              </p>
              <details className="mt-2 text-text-muted">
                <summary className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
                  Evidências e ressalvas da ação
                </summary>
                <p className="mt-1">
                  Fontes: {action.sourceRuleIds.join(', ')} · {action.evidence.length} item(ns)
                  de evidência
                </p>
                {action.caveats.map((caveat) => (
                  <p key={caveat} className="mt-1">
                    Ressalva: {caveat}
                  </p>
                ))}
              </details>
            </li>
          ))}
        </ol>
      </section>
    </aside>
  );
}
